import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@mui/material';
import storage from 'app/services/storage';
import { LOCAL_STORAGE_FORM_KEY } from 'app/shared/components/document/ExtractionField/EditExtractionFields/EditExtractionFieldsForm.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useForm from 'hooks/useForm';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';

import { overrideDocumentExtractionById } from 'store/documents/actions';
import { closeDialogAction, showMessageAction } from 'store/global/actions';
import { getDocumentExtractionFieldsWithNamedType } from 'store/system/selectors';

import { getExtractionFieldsToSend } from '../../../components/document/ExtractionField/EditExtractionFields/EditExtractionFields.utils';
import EditExtractionFieldsFormFieldsPart from '../../../components/document/ExtractionField/EditExtractionFields/EditExtractionFieldsFormFieldsPart';
import { LoanApplicationDocumentsTestIds } from '../LoanApplicationDocuments.testIds';

const EditExtractionFieldsResultsForm = forwardRef(
  (
    {
      documentExtractionResultsInitialForm,
      isLoading,
      setIsLoading,
      documentId,
      documentTypeId = '',
      getExtractionResults,
      hideSaveButton = false,
    },
    ref,
  ) => {
    const dispatch = useDispatch();

    const [duplicatedFields, setDuplicatedFields] = useState([]);
    const [documentExtractionFieldsTree, setDocumentExtractionFieldsTree] = useState([]);

    const documentExtractionFields = useSelector(getDocumentExtractionFieldsWithNamedType);

    const formProps = useForm(documentExtractionResultsInitialForm);
    const { form, validateForm, setForm } = formProps;

    // Synchronous in-flight guard: the tab's footer disables Save on isLoading, but that state
    // only flips AFTER the async validateForm resolves — a double-click within that window would
    // otherwise fire two identical override PUTs. The ref blocks the second call immediately.
    const isSubmittingRef = useRef(false);

    const isButtonDisabled = isLoading || !documentExtractionFields.length;

    const clearLocalStorage = useCallback(() => {
      storage.removeItem(LOCAL_STORAGE_FORM_KEY);
    }, []);

    const onSaveClick = useCallback(async () => {
      if (isSubmittingRef.current) {
        return;
      }

      isSubmittingRef.current = true;
      setIsLoading(true);

      try {
        if (!(await validateForm({ noFormChangedValidate: true }))) {
          return;
        }

        const fields = getExtractionFieldsToSend({
          form,
          documentExtractionFieldsTree,
        });

        if (!fields.extractionFields?.length) {
          dispatch(
            showMessageAction({
              message: 'At least one field should be extracted',
              variant: 'error',
            }),
          );

          return;
        }

        // silent: catchAsyncActionError's generic toast is suppressed so the failure is surfaced
        // once here with the server's own message (e.g. "DocumentExtractionField ein is required").
        await dispatch(
          overrideDocumentExtractionById({
            results: fields.extractionFields,
            documentId,
            silent: true,
          }),
        );
        clearLocalStorage();
        setForm({});

        getExtractionResults();
        dispatch(closeDialogAction());

        dispatch(
          showMessageAction({ message: 'Fields successfully extracted', variant: 'success' }),
        );
      } catch (err) {
        logger.error(err);

        const errorMessage = getDefaultErrorMessage(err);

        dispatch(
          showMessageAction({
            message: _.isEmpty(errorMessage) ? 'Could not save extraction fields' : errorMessage,
            variant: 'error',
          }),
        );
      } finally {
        isSubmittingRef.current = false;
        setIsLoading(false);
      }
    }, [
      validateForm,
      form,
      documentId,
      dispatch,
      setIsLoading,
      clearLocalStorage,
      setForm,
      getExtractionResults,
      documentExtractionFieldsTree,
    ]);

    // Lets a host that renders its own footer (the AI Fields tab's Cancel/Save bar)
    // trigger the save without duplicating the submit pipeline.
    useImperativeHandle(ref, () => ({ submit: onSaveClick }), [onSaveClick]);

    return (
      <>
        <EditExtractionFieldsFormFieldsPart
          isSkipDataLoading
          docId={documentId}
          documentExtractionFieldsTree={documentExtractionFieldsTree}
          documentTypeId={documentTypeId}
          duplicatedFields={duplicatedFields}
          isLoading={isLoading}
          isView={false}
          setDocumentExtractionFieldsTree={setDocumentExtractionFieldsTree}
          setDuplicatedFields={setDuplicatedFields}
          setIsLoading={setIsLoading}
          {...formProps}
        />

        {!hideSaveButton && (
          <div className="flex justify-end">
            <Button
              data-testid={LoanApplicationDocumentsTestIds.EXTRACTION_FIELDS_FORM_SAVE_BUTTON}
              disabled={isButtonDisabled}
              type="button"
              variant="contained"
              onClick={onSaveClick}
            >
              Save
            </Button>
          </div>
        )}
      </>
    );
  },
);

EditExtractionFieldsResultsForm.propTypes = {
  documentExtractionResultsInitialForm: PropTypes.object.isRequired,
  documentId: PropTypes.string.isRequired,
  documentTypeId: PropTypes.string,
  hideSaveButton: PropTypes.bool,
  ...WithLoadingPropTypes,
};

export default EditExtractionFieldsResultsForm;
